import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Disclaimer from './components/Disclaimer';
import Overview from './components/Overview';
import AdminAllowedGroups from './components/AdminAllowedGroups';
import AdminUsers from './components/AdminUsers';
import AccessDenied from './components/AccessDenied';
import LoadingSpinner from './components/LoadingSpinner';
import Layout from './components/Layout';
import BetaLayout from './components/beta/BetaLayout';
import BetaOverview from './components/beta/BetaOverview';
import { AuthProvider, useAuth } from './context/AuthContext';
import { onSignIn, onSignOut } from './utils/AuthFunctionUtils';
import { UserDataContextProvider } from './context/UserDataContext';
import { AirlineProvider } from './context/AirlineContext';
import SplitProvider from './context/SplitProvider';
import { useTreatments } from '@splitsoftware/splitio-react';
import { fetchAllAllowedGroups } from './services/allowedGroupsService';
import { fetchAdminUsers, checkUserAccess } from './services/usersService';
import { appInsights } from './appInsights';
import { 
  ROUTES, 
  STORAGE_KEYS, 
  APP_INSIGHTS_EVENTS 
} from './constants/appConstants';
import './styles/App.scss';

const App: React.FC = () => {
  return (
    <AuthProvider onSignIn={onSignIn} onSignOut={onSignOut}>
      <UserDataContextProvider>
        <SplitProvider>
          <AirlineProvider>
            <div className="app-container">
              <Router>
                <AppRoutes />
              </Router>
            </div>
          </AirlineProvider>
        </SplitProvider>
      </UserDataContextProvider>
    </AuthProvider>
  );
};

export const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, userData } = useAuth();
  const { treatments, isReady: splitReady, hasTimedout: splitTimedout } = useTreatments({ names: ['ACARS_2_0', 'ACARS_2_0_API'] });
  const splitSettled = splitReady || splitTimedout;
  const acars2Enabled = treatments?.['ACARS_2_0'] === 'on';
  const acars2ApiEnabled = treatments?.['ACARS_2_0_API'] === 'on';
  const [disclaimerAccepted, setDisclaimerAccepted] = useState<boolean>(
    !!localStorage.getItem(STORAGE_KEYS.DISCLAIMER_ACCEPTED)
  );
  const [allowedGroups, setAllowedGroups] = useState<string[]>([]);
  const [adminUsers, setAdminUsers] = useState<string[]>([]);
  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);
  const [brandAirlines, setBrandAirlines] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(true);
  const [hasCheckedAccess, setHasCheckedAccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationInProgress = useRef(false);

  const allowedAdminUsers = useMemo(() => new Set(adminUsers), [adminUsers]);

  const isAdminUser = useMemo(() => {
    const userEmail = (userData?.profile?.email || '').toLowerCase();
    // Convert all admin emails to lowercase for case-insensitive comparison
    const adminEmailsLowerCase = new Set(Array.from(allowedAdminUsers).map(email => email.toLowerCase()));
    const isAdmin = adminEmailsLowerCase.has(userEmail);
    //console.log('isAdminUser check - userEmail:', userEmail, 'allowedAdminUsers:', Array.from(allowedAdminUsers), 'isAdmin:', isAdmin);
    return isAdmin;
  }, [userData?.profile?.email, allowedAdminUsers]);

  const loadAllowedGroups = useCallback(async () => {
    if (isAuthenticated && userData?.access_token) {
      try {
        // Fetch allowed groups
        const groups = await fetchAllAllowedGroups(userData.access_token);
        const activeGroupNames = (groups || [])
          .filter(g => g.isActive)
          .map(g => g.groupName);
        setAllowedGroups(activeGroupNames);

        // Fetch admin users
        const admins = await fetchAdminUsers(userData.access_token);
        //console.log('Fetched admin users:', admins);
        setAdminUsers(admins);

        // Check if current user has access by email
        const userEmail = userData.profile?.email || '';
        //console.log('Current user email:', userEmail);
        if (userEmail) {
          const accessResult = await checkUserAccess(userData.access_token, userEmail);
          //console.log('User has access:', accessResult);
          setUserHasAccess(accessResult.valid);
          setBrandAirlines(accessResult.brandAirlines || []);
        } else {
          setUserHasAccess(false);
          setBrandAirlines([]);
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Failed to load allowed groups:', error);
        // On API failure, DON'T clear existing data or deny access
        // This prevents intermittent API failures from blocking valid users
        // Keep previous allowedGroups and userHasAccess values if they exist
        // Only set to null if this is the first load attempt (no data yet)
      } finally {
        setGroupsLoading(false);
      }
    } else {
      setGroupsLoading(false);
    }
  }, [isAuthenticated, userData?.access_token, userData?.profile?.email]);

  useEffect(() => {
    loadAllowedGroups();
  }, [loadAllowedGroups]);

  useEffect(() => {
    // Prevent navigation during an ongoing navigation
    if (navigationInProgress.current) {
      return;
    }

    // Only run navigation logic once groups are loaded, auth is complete, and Split SDK is settled
    if (isLoading || groupsLoading || !splitSettled) {
      return;
    }

    if (!isAuthenticated) {
      if (location.pathname !== ROUTES.ROOT && location.pathname !== ROUTES.CALLBACK) {
        navigationInProgress.current = true;
        navigate(ROUTES.ROOT, { replace: true });
        setTimeout(() => { navigationInProgress.current = false; }, 100);
      }
      return;
    }

    if (!userData) {
      return;
    }

    const groups: string[] = (userData.profile?.groups as string[]) || [];
    const userName = `${userData.profile?.given_name || "Unknown"} ${userData.profile?.family_name || ""}`.trim();
    const userEmail = userData.profile?.email || "Unknown";
    const userGroups: string[] = (userData.profile?.groups as string[]) || [];

    // Check if user has any matching groups with allowed groups (case-insensitive)
    const allowedGroupsLower = allowedGroups.map(g => g.toLowerCase());
    const hasMatchingGroups = userGroups.some(group => allowedGroupsLower.includes(group.toLowerCase()));

    // User is allowed if they exist in Users table with isActive=true OR have matching groups
    // Admin access via email check is mandatory, but regular users can access via group membership
    const isUserAllowed = userHasAccess === true || hasMatchingGroups;
    
    // Mark that we've checked access at least once
    if (!hasCheckedAccess) {
      setHasCheckedAccess(true);
    }
    
    // Log users who successfully access the overview page (only once per session)
    if ((location.pathname === ROUTES.OVERVIEW || location.pathname === ROUTES.BETA_OVERVIEW) && isUserAllowed) {
      const logKey = `overviewVisit_${userEmail}`;
      if (!sessionStorage.getItem(logKey)) {
        appInsights.trackEvent({
          name: APP_INSIGHTS_EVENTS.OVERVIEW_PAGE_VISIT,
          properties: {
            timestamp: new Date().toISOString(),
            userName,
            userEmail,
            userGroups: userGroups.join(", "),
          },
        });
        sessionStorage.setItem(logKey, 'true');
      }
    }

    // Navigation logic with guards to prevent loops
    // Priority 1: Check if user is allowed at all (only after groups are loaded)
    // User must have email in Users table with isActive=true OR have matching groups with allowed groups
    const isOnProtectedPage = location.pathname === ROUTES.OVERVIEW || location.pathname === ROUTES.BETA_OVERVIEW || location.pathname === ROUTES.ADMIN || location.pathname === ROUTES.ADMIN_USERS;
    const hasCompletedAccessCheck = userHasAccess !== null;
    const shouldDenyAccess = !isUserAllowed && hasCompletedAccessCheck;
    
    if (shouldDenyAccess) {
      // Only redirect to access denied if:
      // 1. We've checked access before, OR
      // 2. User is not already on a protected page (initial load scenario)
      if ((hasCheckedAccess || !isOnProtectedPage) && location.pathname !== ROUTES.ACCESS_DENIED) {
        navigationInProgress.current = true;
        navigate(ROUTES.ACCESS_DENIED, { replace: true });
        setTimeout(() => { navigationInProgress.current = false; }, 100);
      }
      return;
    }

    // Priority 2: Check admin access for admin routes
    if (location.pathname === ROUTES.ADMIN || location.pathname === ROUTES.ADMIN_USERS) {
      if (!isAdminUser) {
        navigationInProgress.current = true;
        navigate(ROUTES.ACCESS_DENIED, { replace: true });
        setTimeout(() => { navigationInProgress.current = false; }, 100);
      }
      // If user IS admin, let them stay on admin page - no navigation needed
      return;
    }

    // Priority 3: Handle disclaimer flow
    if (!disclaimerAccepted) {
      if (location.pathname !== ROUTES.CALLBACK) {
        navigationInProgress.current = true;
        navigate(ROUTES.CALLBACK, { replace: true });
        setTimeout(() => { navigationInProgress.current = false; }, 100);
      }
      return;
    }

    // Priority 4: If disclaimer is accepted and user is on callback, go to overview
    if (disclaimerAccepted && location.pathname === ROUTES.CALLBACK) {
      navigationInProgress.current = true;
      navigate(acars2Enabled ? ROUTES.BETA_OVERVIEW : ROUTES.OVERVIEW, { replace: true });
      setTimeout(() => { navigationInProgress.current = false; }, 100);
      return;
    }

    // Default: Ensure user is on the correct overview version based on the feature flag.
    // Handles: initial load (ROOT/CALLBACK), page refresh on the wrong overview, and
    // the case where the Split SDK settles after initial navigation has already occurred.
    const onWrongOverview =
      (acars2Enabled && location.pathname === ROUTES.OVERVIEW) ||
      (!acars2Enabled && location.pathname === ROUTES.BETA_OVERVIEW);

    if (location.pathname === ROUTES.ROOT || location.pathname === ROUTES.CALLBACK || onWrongOverview) {
      navigationInProgress.current = true;
      navigate(acars2Enabled ? ROUTES.BETA_OVERVIEW : ROUTES.OVERVIEW, { replace: true });
      setTimeout(() => { navigationInProgress.current = false; }, 100);
    }
  }, [
    isAuthenticated, 
    isLoading, 
    groupsLoading, 
    allowedGroups.length,
    userHasAccess,
    disclaimerAccepted, 
    location.pathname, 
    userData?.profile?.email,
    isAdminUser,
    navigate,
    hasCheckedAccess,
    acars2Enabled,
    splitSettled,
  ]);
  if (isLoading || groupsLoading || !splitSettled) {
    return <LoadingSpinner size="large" />;
  }

  const renderAdminPage = (children: React.ReactNode) => {
    if (!isAuthenticated) {
      return <Navigate to={ROUTES.CALLBACK} replace />;
    }

    const AdminLayout = acars2Enabled ? BetaLayout : Layout;
    return (
      <AdminLayout isAdmin={isAdminUser} brandAirlines={brandAirlines}>
        {children}
      </AdminLayout>
    );
  };

  return (
    <Routes>
      <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.CALLBACK} replace />} />
      <Route 
        path={ROUTES.CALLBACK} 
        element={
          isAuthenticated 
            ? <Disclaimer onAccept={() => setDisclaimerAccepted(true)} /> 
            : <Navigate to={ROUTES.ROOT} replace />
        } 
      />
      <Route 
        path={ROUTES.OVERVIEW} 
        element={
          <Layout isAdmin={isAdminUser} brandAirlines={brandAirlines}>
            <Overview isAdmin={isAdminUser} />
          </Layout>
        } 
      />
      <Route 
        path={ROUTES.BETA_OVERVIEW} 
        element={
          <BetaLayout isAdmin={isAdminUser} brandAirlines={brandAirlines}>
            <BetaOverview isAdmin={isAdminUser} useExtendedSearchApi={acars2ApiEnabled} />
          </BetaLayout>
        } 
      />
      <Route 
        path={ROUTES.ADMIN} 
        element={
          renderAdminPage(<AdminAllowedGroups />)
        } 
      />
      <Route 
        path={ROUTES.ADMIN_USERS} 
        element={
          renderAdminPage(<AdminUsers />)
        } 
      />
      <Route path={ROUTES.ACCESS_DENIED} element={<AccessDenied />} />
      <Route 
        path="*" 
        element={<Navigate to={ROUTES.CALLBACK} replace />} 
      />
    </Routes>
  );
};

export default App;
